import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Cloud,
  Download,
  FileText,
  Loader2,
  MessageSquareText,
  Pencil,
  Trash2,
  Users,
} from "lucide-react";
import {
  useDeleteProject,
  useProject,
  useProjectMembers,
} from "@/hooks/use-projects";
import { useAuth } from "@/components/providers/auth-provider";
import { ApiError, fetchBlobWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ErrorState } from "@/components/ui/error-state";
import { DocumentSection } from "@/components/documents/document-section";
import { SharePointSection } from "@/components/sources/sharepoint-section";
import { InvoicesSection } from "@/components/invoices/invoices-section";
import { MeasurementsSection } from "@/components/measurements/measurements-section";
import ChatInterface from "@/components/chat/chat-interface";
import { ProjectMembersDialog } from "@/components/projects/project-members-dialog";
import { ProjectEditDialog } from "@/components/projects/project-edit-dialog";
import { cn } from "@/lib/utils";

type Tab = "documentos" | "chat" | "sharepoint";
type DocView = "todos" | "facturas" | "mediciones";

export default function ProyectoDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const tab: Tab =
    tabParam === "chat"
      ? "chat"
      : tabParam === "sharepoint"
        ? "sharepoint"
        : "documentos";
  const setTab = (next: Tab) =>
    setSearchParams(next === "documentos" ? {} : { tab: next }, { replace: true });

  const projectQuery = useProject(id);
  const membersQuery = useProjectMembers(id);
  const deleteProject = useDeleteProject();

  const [membersOpen, setMembersOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [docView, setDocView] = useState<DocView>("todos");

  // owner/admin pueden gestionar miembros y borrar el proyecto.
  const canManage = useMemo(() => {
    if (isAdmin) return true;
    if (projectQuery.data?.my_role === "owner") return true;
    const me = (membersQuery.data ?? []).find((m) => m.email === user?.email);
    return me?.role === "owner";
  }, [isAdmin, projectQuery.data, membersQuery.data, user?.email]);

  // Descarga (solo admin) del dataset de fine-tuning (correcciones + 👍) en JSONL.
  const downloadDataset = async () => {
    try {
      const blob = await fetchBlobWithAuth("/api/v1/chat/finetune-dataset");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "finetune-cuerva.jsonl";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast.success("Dataset descargado");
    } catch {
      showToast.error("No se pudo descargar el dataset");
    }
  };

  if (projectQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 text-content-muted animate-spin" />
      </div>
    );
  }

  if (projectQuery.isError || !projectQuery.data) {
    const status =
      projectQuery.error instanceof ApiError ? projectQuery.error.status : null;
    const denied = status === 403 || status === 404;
    return (
      <div className="max-w-container mx-auto px-4 py-6 space-y-4">
        <ErrorState
          message={
            denied
              ? "No tienes acceso a este proyecto o no existe. Pide a su propietario que te añada como miembro."
              : projectQuery.error?.message ?? "Proyecto no encontrado"
          }
          onRetry={denied ? undefined : () => projectQuery.refetch()}
        />
        <Link
          to="/proyectos"
          className="inline-flex items-center gap-1 text-sm text-content-muted hover:text-ink"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a proyectos
        </Link>
      </div>
    );
  }

  const project = projectQuery.data;

  return (
    <div className="max-w-container mx-auto px-4 lg:px-6 py-6 flex flex-col h-full space-y-4">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link
            to="/proyectos"
            className="inline-flex items-center gap-1 text-xs text-content-muted hover:text-ink mb-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Proyectos
          </Link>
          <h1 className="text-2xl font-heading font-semibold text-ink truncate">
            {project.nombre}
          </h1>
          {project.descripcion && (
            <p className="text-sm text-content-muted mt-0.5 max-w-2xl">
              {project.descripcion}
            </p>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil className="w-4 h-4" />
              Editar
            </Button>
            <Button variant="outline" onClick={() => setMembersOpen(true)}>
              <Users className="w-4 h-4" />
              Miembros
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={downloadDataset}
                title="Descargar dataset de fine-tuning (correcciones + 👍) en JSONL"
              >
                <Download className="w-4 h-4" />
                Dataset IA
              </Button>
            )}
            <Button
              variant="outline"
              className="text-danger hover:text-danger"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-line flex gap-1">
        <button
          onClick={() => setTab("documentos")}
          className={cn(
            "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-heading border-b-2 -mb-px transition-colors",
            tab === "documentos"
              ? "border-energia text-ink"
              : "border-transparent text-content-muted hover:text-ink"
          )}
        >
          <FileText className="w-4 h-4" />
          Documentos
        </button>
        <button
          onClick={() => setTab("chat")}
          className={cn(
            "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-heading border-b-2 -mb-px transition-colors",
            tab === "chat"
              ? "border-energia text-ink"
              : "border-transparent text-content-muted hover:text-ink"
          )}
        >
          <MessageSquareText className="w-4 h-4" />
          Chat
        </button>
        {canManage && (
          <button
            onClick={() => setTab("sharepoint")}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-heading border-b-2 -mb-px transition-colors",
              tab === "sharepoint"
                ? "border-energia text-ink"
                : "border-transparent text-content-muted hover:text-ink"
            )}
          >
            <Cloud className="w-4 h-4" />
            SharePoint
          </button>
        )}
      </div>

      {/* Contenido */}
      {tab === "documentos" ? (
        <div className="space-y-4">
          {canManage && (
            <div className="flex items-center gap-2">
              <label htmlFor="docview" className="text-xs text-content-muted">
                Ver:
              </label>
              <select
                id="docview"
                value={docView}
                onChange={(e) => setDocView(e.target.value as DocView)}
                className="text-sm rounded-md border border-line bg-surface px-2.5 py-1.5 text-ink focus:outline-none focus:ring-2 focus:ring-brand/20"
              >
                <option value="todos">Todos los documentos</option>
                <option value="facturas">Facturas</option>
                <option value="mediciones">Mediciones</option>
              </select>
            </div>
          )}
          {canManage && docView === "facturas" ? (
            <InvoicesSection projectId={id} />
          ) : canManage && docView === "mediciones" ? (
            <MeasurementsSection projectId={id} />
          ) : (
            <DocumentSection projectId={id} />
          )}
        </div>
      ) : tab === "sharepoint" ? (
        <SharePointSection projectId={id} />
      ) : (
        <div className="flex-1 min-h-[60vh]">
          <ChatInterface projectId={id} />
        </div>
      )}

      {editOpen && (
        <ProjectEditDialog
          open
          onClose={() => setEditOpen(false)}
          projectId={id}
          initialNombre={project.nombre}
          initialDescripcion={project.descripcion ?? ""}
        />
      )}

      <ProjectMembersDialog
        projectId={id}
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
        canManage={canManage}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Eliminar proyecto"
        description={`Se eliminará «${project.nombre}» con todos sus documentos, chunks y conversaciones. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar proyecto"
        variant="destructive"
        loading={deleteProject.isPending}
        onConfirm={() =>
          deleteProject.mutate(id, {
            onSuccess: () => navigate("/proyectos", { replace: true }),
          })
        }
      />
    </div>
  );
}
